import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Permission } from '../../../../__generated__/graphql';
import { assetUrl } from '../../../../common/helpers';
import { useSession } from '../../../Auth/SessionProvider';
import GetParticipantOrdersQuery from './GetParticipantOrdersQuery';
import OrderDetails from './OrderDetails';
import UI from '../../../../common/UI';
import useAddOrderModal from './useAddOrderModal';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';

export interface OrdersPageProps {
  participantId: string;
  orderId?: string;
}

const OrdersPage = ({ participantId, orderId }: OrdersPageProps) => {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const project = useProject();
  const history = useHistory();

  const { data } = useQuery(
    GetParticipantOrdersQuery,
    {
      variables: {
        project_id: project.id,
        participant_id: participantId,
      },
    },
  );

  const [openAddOrderModal] = useAddOrderModal({
    participantId,
    onSuccess: (order) => {
      history.push(`/${project.id}/participants/${participantId}/orders/${order.id}`);
    },
  });

  const participant = data?.project.participant;
  const orders = participant?.orders;

  useEffect(() => {
    if (orderId && orders && !orders.some((order) => order.id === orderId)) {
      history.push(`/${project.id}/participants/${participantId}/orders`);
    }
  }, [orderId, orders, history, project, participantId]);

  const hasEditPermission = hasPermission(project.id, [Permission.edit_registrations]);

  if (!data) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.FlexContainer sc={{ flex: { justifyContent: 'space-between', alignItems: 'center' } }}>
          <UI.SectionHeading>
            {t('orders')}
          </UI.SectionHeading>
          {hasEditPermission && orders.length > 0 && participant.active && (
            <UI.Button onClick={() => openAddOrderModal()} sc={{ brand: 'secondary' }}>
              {t('add_order')}
            </UI.Button>
          )}
        </UI.FlexContainer>

        {orders.length > 0 && (
          <UI.GridContainer sc={{ gutter: 1 / 3 }}>
            {orders.map((order) => (
              <OrderDetails
                order={order}
                participant={participant}
                isActive={orderId === order.id}
                isMuted={!!orderId && orderId !== order.id}
                key={order.id}
              />
            ))}
          </UI.GridContainer>
        )}

        {orders.length === 0 && (
          <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }} style={{ textWrap: 'balance' }}>
            <img src={assetUrl('images/down-payments.svg')} alt="" height={120} />
            <UI.Paragraph>{t('orders_get_started')}</UI.Paragraph>
            {hasEditPermission && participant.active && (
              <UI.Button
                onClick={openAddOrderModal}
                sc={{ brand: 'secondary' }}
              >
                {t('add_order')}
              </UI.Button>
            )}
          </UI.Card>
        )}
      </UI.GridContainer>
    </UI.Section>
  );
};

export default OrdersPage;
